import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { gridGapStyles, linkStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state, { maxDepth: 2 }),
    topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface SubCategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
}

export const subCategoryListStyles: SubCategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 0,
        css: css`
            ${gridGapStyles(["24px", "24px", "32px", "40px"], ["20px"])}
        `
    },
    categoryItem: {
        width: [12, 6, 4, 3, 3],
    },
    innerContainer: {
        gap: 0,
        css: css`
            height: 100%;
            border: 1px solid ${getColor("common.border")};
            padding: 30px;
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 16px;
        `,
    },
    categoryName: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: ${TYPOGRAPHY.size.large};
            color: ${getColor("secondary.base")};
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    subCategoryNameLink: {
        typographyProps: {
            lineHeight: TYPOGRAPHY.lineHeight.small,
        },
        css: css`
            padding: 3px 0;
            ${linkStyles("text", "text.link", false)}
        `
    },
   
};

const styles = subCategoryListStyles;

class SubCategoryList extends React.Component<Props> {
    componentDidMount(): void {
        const { categoriesDataView, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories({ maxDepth: 2 });
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value) {
            return null;
        }

        const categories = categoriesDataView.value.filter(category => !topLevelCategoryIds?.includes(category.id));

        return (
            <GridContainer {...styles.container}>
                {categories.map(category => (
                    <GridItem key={category.id.toString()} {...styles.categoryItem}>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem {...styles.categoryNameLinkItem}>
                                <Link href={category.path} {...styles.categoryNameLink}>
                                    <Typography {...styles.categoryName}>{category.shortDescription}</Typography>
                                </Link>
                            </GridItem>
                            {category.subCategoryIds?.map(subCategoryId => (
                                <GridItem key={subCategoryId} {...styles.subCategoryNameLinkItem}>
                                    <SubCategoryLink categoryId={subCategoryId}  />
                                </GridItem>
                            ))}
                        </GridContainer>
                    </GridItem>
                ))}
            </GridContainer>
        );
    }
}

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

type SubCategoryLinkProps = ReturnType<typeof linkMapStateToProps>;

const SubCategoryLinkView = (props: SubCategoryLinkProps) => {
	const { category } = props;

    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
          {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SubCategoryList),
    definition: {
        group: "Categories",
        icon: "list-ul",
    },
};

export default widgetModule;
